import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AuthorChip from "./AuthorChip";
import ReactionBar from "./ReactionBar";
import PollDisplay from "./PollDisplay";
import CommentsThread from "./CommentsThread";
import { MediaGallery, VideoEmbed, LinkPreview } from "./PostMedia";
import RichText from "./RichText";
import { useAuth } from "../contexts/AuthContext";
import { MessageSquare, Share2, Trash2, Pencil, Check, X, Bookmark, BookmarkCheck as BookmarkSolid } from "lucide-react";

export default function PostCard({ post, onDeleted, onUpdated, onUnsaved }) {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [shared, setShared] = useState(false);
  const [poll, setPoll] = useState(post.poll);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(post.content || "");
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [saved, setSaved] = useState(null); // null = unknown / loading
  const [savingBookmark, setSavingBookmark] = useState(false);

  const isOwner = user && (post.author?.id === user.id || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/posts/${post.id}/is-saved`)
      .then((r) => { if (!cancelled) setSaved(!!r.data.saved); })
      .catch(() => { if (!cancelled) setSaved(false); });
    return () => { cancelled = true; };
  }, [post.id]);

  const toggleSave = async () => {
    setSavingBookmark(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/save`);
      setSaved(data.saved);
      if (!data.saved) onUnsaved?.(post.id);
    } catch (err) {
      console.warn("[save] failed:", err?.response?.status || err?.message);
    } finally {
      setSavingBookmark(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/post/${post.id}`;
    try { await navigator.clipboard.writeText(url); } catch (err) {
      console.warn("[share] clipboard write blocked:", err?.message);
    }
    try {
      await api.post(`/posts/${post.id}/share`, {});
    } catch (err) {
      console.warn("[share] count update failed:", err?.response?.status || err?.message);
    }
    setShared(true);
    setTimeout(() => setShared(false), 1800);
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/posts/${post.id}`);
    onDeleted?.(post.id);
  };

  const saveEdit = async () => {
    setSaving(true); setEditErr("");
    try {
      await api.patch(`/posts/${post.id}`, { content: editValue });
      onUpdated?.({ ...post, content: editValue, is_edited: true, edited_at: new Date().toISOString() });
      setEditing(false);
    } catch (e) { setEditErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <article className="csc-card csc-overflow-safe p-5" data-testid={`post-${post.id}`}>
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <AuthorChip author={post.author} timestamp={post.created_at} />
          {post.is_edited && <span className="ml-1 text-[10px] font-mono-tech text-[#94a3b8]" data-testid={`edited-label-${post.id}`}>(edited)</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={toggleSave}
            disabled={savingBookmark || saved === null}
            className={`p-1 ${saved ? "text-[#00aaff]" : "text-[#94a3b8] hover:text-[#00aaff]"}`}
            title={saved ? "Saved — click to remove" : "Save for later"}
            data-testid={`bookmark-post-${post.id}`}
            aria-pressed={!!saved}
          >
            {saved ? <BookmarkSolid size={14} fill="currentColor" /> : <Bookmark size={14} />}
          </button>
          {isOwner && !editing && (
            <button onClick={() => { setEditing(true); setEditValue(post.content || ""); }} className="text-[#94a3b8] hover:text-[#00aaff] p-1" data-testid={`edit-post-${post.id}`} title="Edit post">
              <Pencil size={14} />
            </button>
          )}
          {(post.can_delete || isOwner) && (
            <button onClick={remove} className="text-[#94a3b8] hover:text-red-400 p-1" data-testid={`delete-post-${post.id}`} title="Delete post">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={4} className="csc-input csc-break-anywhere resize-none" data-testid={`edit-textarea-${post.id}`} />
          {editErr && <div className="text-xs text-red-400">{editErr}</div>}
          <div className="flex gap-2">
            <button onClick={saveEdit} disabled={saving || !editValue.trim()} className="csc-btn-primary !py-1.5" data-testid={`save-edit-${post.id}`}><Check size={13} /> Save</button>
            <button onClick={() => setEditing(false)} className="csc-btn-ghost !py-1.5" data-testid={`cancel-edit-${post.id}`}><X size={13} /> Cancel</button>
          </div>
        </div>
      ) : (
        post.content && (
          <div className="mt-3 text-[15px] leading-relaxed whitespace-pre-wrap text-white csc-break-anywhere">
            <RichText text={post.content} />
          </div>
        )
      )}

      <MediaGallery media={post.media} />
      <VideoEmbed embed={post.video_embed} />
      <LinkPreview preview={post.link_preview} />

      <PollDisplay
        postId={post.id}
        poll={poll}
        onChange={(updated) => {
          setPoll(updated.poll);
          onUpdated?.(updated);
        }}
      />

      <div className="mt-4 pt-3 border-t border-[rgba(0,170,255,0.18)] flex items-center justify-between gap-2 flex-wrap">
        <ReactionBar postId={post.id} count={post.counts?.reactions || 0} myReaction={post.my_reaction} />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowComments((v) => !v)}
            className="csc-btn-ghost !py-1.5 !px-3"
            data-testid={`toggle-comments-${post.id}`}
          >
            <MessageSquare size={13} /> {post.counts?.comments || 0}
          </button>
          <button
            onClick={share}
            className="csc-btn-ghost !py-1.5 !px-3"
            data-testid={`share-post-${post.id}`}
          >
            <Share2 size={13} /> {shared ? "Copied!" : "Share"}
          </button>
        </div>
      </div>

      {showComments && (
        <div className="mt-3 pt-3 border-t border-[rgba(0,170,255,0.18)]">
          <CommentsThread postId={post.id} />
        </div>
      )}
    </article>
  );
}
